// src/ui/gate/GateScreen.tsx — password gate entry screen.
// UI-09, D-01, D-02, D-03. UI-SPEC §Gate Screen.
// Submits via onSubmit prop; parent owns the auth fetch + error state
// (useGateAuth). This separation keeps the component trivially testable.
//
// P5 Plan 05-11: `mode` prop controls cosmetic copy only (wordmark,
// placeholder, submit label). Form structure, error UX, shake animation,
// and submit semantics are identical across modes — the hook layer
// (useGateAuth) owns the role wiring, so GateScreen is role-agnostic at
// its interface (threat T-05-G11-04).

import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';

import '../shell.css';
import { audioManager } from '../../render/audio/manager';

type GateMode = 'game' | 'admin';

interface GateScreenProps {
  error: string | null;
  onSubmit(password: string): Promise<void>;
  mode?: GateMode;
}

const COPY: Record<
  GateMode,
  { wordmark: string; placeholder: string; submit: string }
> = {
  game: {
    wordmark: 'Mouchao',
    placeholder: 'Password',
    submit: 'Enter',
  },
  admin: {
    wordmark: 'Mouchao Admin',
    placeholder: 'Admin password',
    submit: 'Enter admin',
  },
};

export function GateScreen({
  error,
  onSubmit,
  mode = 'game',
}: GateScreenProps): JSX.Element {
  const copy = COPY[mode];
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Trigger shake animation when error transitions from falsy to truthy.
  useEffect(() => {
    if (error) {
      setShake(true);
      inputRef.current?.focus();
    }
  }, [error]);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (pending || password.length === 0) return;
    // P6 D-18: fire iOS Web Audio unlock inside the user-gesture frame.
    // Submit IS the gesture; the call must be synchronous here, NOT inside
    // a setTimeout / promise callback, or iOS Safari rejects ctx.resume().
    audioManager.unlock();
    setPending(true);
    try {
      await onSubmit(password);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="gate-screen" data-testid="gate-screen" data-mode={mode}>
      <div className="gate-screen__column">
        <img
          className="gate-screen__vignette"
          src="/splash-placeholder.png"
          alt=""
          aria-hidden="true"
        />
        <h1 className="gate-screen__wordmark">{copy.wordmark}</h1>
        <form
          className="gate-screen__form"
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
        >
          <label htmlFor="hunter-password" className="sr-only">
            {copy.placeholder}
          </label>
          <input
            ref={inputRef}
            id="hunter-password"
            data-testid="gate-input"
            className={`gate-screen__input${shake ? ' shake' : ''}`}
            type="password"
            autoComplete="current-password"
            placeholder={copy.placeholder}
            value={password}
            onChange={(e) => {
              setPassword(e.currentTarget.value);
              if (shake) setShake(false); // reset shake on first key
            }}
            onAnimationEnd={() => setShake(false)}
            disabled={pending}
          />
          <button
            type="submit"
            data-testid="gate-submit"
            className="gate-screen__button"
            disabled={pending || password.length === 0}
          >
            {copy.submit}
          </button>
          {error !== null && (
            <p
              className="gate-screen__error"
              role="alert"
              data-testid="gate-error"
            >
              {error}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
